import { LabelChip, Tooltip } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type {
  TrainingEvalMetricsView,
  TrainingEvalSuccessTermsView,
  TrainingImageMetadataTaskView,
} from "@/api/trainingEval";
import type { LoadState } from "@/components/dataset";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { EvalRunDetailSection } from "./EvalRunDetailElements";
import { EvalSuccessCriteria, EvalSuccessCriteriaChips } from "./EvalSuccessCriteria";
import { formatEpisodeSeconds, successfulEpisodeTime } from "./episodeTimeModel";
import { successCriteriaRows, type RunExecution } from "./evalRunScenarioModel";
import { isaacStages, isaacSuccessRates, type IsaacStage } from "./isaacEvalModel";
import type { EvalImageMetadataState } from "./useEvalImageMetadata";
import styles from "./IsaacEvalSection.module.css";

export const ISAAC_SUCCESS_NOTE =
  "Object·Robot 조건은 Episode 종료 시 각각 판정합니다. 같은 Episode가 두 조건을 모두 충족할 수 있습니다.";
const ISAAC_TASK_SUCCESS_NOTE =
  "Task Success Rate는 종료 시 TASK의 전체 성공 조건을 만족한 비율입니다. Policy 완료 선언을 함께 확인하는 최종 성공률과 다를 수 있습니다.";
export const ISAAC_STAGE_NOTE =
  "Stage 도달률은 Episode 중 해당 단계에 한 번 이상 도달한 비율입니다.";
export const ISAAC_TIME_NOTE =
  "성공 평균 시간은 시간을 측정한 성공 Episode만 집계합니다. 시뮬레이션에서 done을 선언한 시간이며 inference chunk 간격의 영향을 받습니다. Reset 이후 미측정 값은 제외합니다.";

export function metadataTask(state: EvalImageMetadataState, task: string | null) {
  return state.status === "success"
    ? state.data?.tasks.find((candidate) => candidate.name === task)
    : undefined;
}

export function IsaacTaskLabel({
  name,
  task,
}: {
  name: string | null;
  task?: TrainingImageMetadataTaskView;
}) {
  return (
    <span className={styles.task}>
      <span className={styles.wrapText}>{name ?? "-"}</span>
      {task?.kind === "sandbox" ? (
        <LabelChip tone="neutral" variant="filled" size="xs" showIcon={false}>
          Sandbox
        </LabelChip>
      ) : null}
    </span>
  );
}

export function IsaacMetadataNotice({
  execution,
  state,
  onRetry,
}: {
  execution: RunExecution;
  state: EvalImageMetadataState;
  onRetry: () => void;
}): ReactNode {
  if (execution.tag === null)
    return <p className={styles.note}>Image tag가 없어 TASK metadata를 확인할 수 없습니다.</p>;
  if (state.status === "idle" || state.status === "loading")
    return <LoadingState size="sm">TASK metadata를 불러오는 중입니다…</LoadingState>;
  if (state.status === "error")
    return (
      <EmptyState size="sm" tone="error" action={{ label: "Metadata 다시 시도", onClick: onRetry }}>
        TASK metadata를 불러오지 못했습니다.
      </EmptyState>
    );
  if (state.data === null)
    return <p className={styles.note}>이 image는 TASK metadata를 제공하지 않습니다.</p>;
  if (metadataTask(state, execution.task) === undefined)
    return (
      <p className={styles.note}>
        Image metadata에서 TASK {execution.task ?? "-"}를 찾을 수 없습니다.
      </p>
    );
  return null;
}

export function isaacPercent(value: number | null) {
  return value === null ? "-" : `${(value * 100).toFixed(1)}%`;
}

export function successfulTimeLabel(
  state: LoadState<TrainingEvalSuccessTermsView> | undefined,
): string {
  if (state === undefined || state.status === "idle" || state.status === "loading")
    return "불러오는 중…";
  if (state.status === "error") return "조회 실패";
  const time = successfulEpisodeTime(state.data.episodes);
  return time.mean === null
    ? "미측정"
    : `${formatEpisodeSeconds(time.mean)} s (${time.measured}개)`;
}

export function IsaacStageList({ stages }: { stages: readonly IsaacStage[] }) {
  if (stages.length === 0) return null;
  return (
    <div className={styles.stageSection}>
      <h4 className={styles.subtitle}>Stage 도달률</h4>
      <ol className={styles.stages}>
        {stages.map((stage) => (
          <li key={stage.name} className={styles.stage}>
            <span className={styles.wrapText}>{stage.name}</span>
            <span className={styles.track} aria-hidden="true">
              {stage.value === null ? null : (
                <span
                  className={styles.fill}
                  style={{ width: `${Math.max(0, Math.min(1, stage.value)) * 100}%` }}
                />
              )}
            </span>
            <span className={styles.number}>{isaacPercent(stage.value)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function IsaacEvalSection({
  execution,
  metadataState,
  metricsState,
  termsState,
  onRetry,
}: {
  execution: RunExecution;
  metadataState: EvalImageMetadataState;
  metricsState: LoadState<TrainingEvalMetricsView>;
  termsState: LoadState<TrainingEvalSuccessTermsView>;
  onRetry: () => void;
}) {
  const task = metadataTask(metadataState, execution.task);
  const metrics = metricsState.status === "success" ? metricsState.data.metrics_all : {};
  const stages = isaacStages(task, metrics);
  const rates = isaacSuccessRates(
    metrics,
    termsState.status === "success" ? termsState.data : undefined,
  );
  const criteria = successCriteriaRows(task);
  const criteriaByTerm = {
    object_success: criteria.filter((criterion) => criterion.name.startsWith("object.")),
    robot_success: criteria.filter((criterion) => criterion.name.startsWith("robot.")),
  };
  const additionalCriteria = criteria.filter(
    (criterion) => !criterion.name.startsWith("object.") && !criterion.name.startsWith("robot."),
  );
  return (
    <EvalRunDetailSection title="Isaac Evaluation">
      <div className={styles.task}>
        TASK <IsaacTaskLabel name={execution.task} task={task} />
      </div>
      <IsaacMetadataNotice execution={execution} state={metadataState} onRetry={onRetry} />
      {metricsState.status === "idle" || metricsState.status === "loading" ? (
        <LoadingState size="sm">Isaac 지표를 불러오는 중입니다…</LoadingState>
      ) : metricsState.status === "error" ? (
        <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: onRetry }}>
          Isaac 지표를 불러오지 못했습니다.
        </EmptyState>
      ) : (
        <figure className={styles.rateChart} aria-label="Isaac Success Rates">
          <div className={styles.rateAxis} aria-hidden="true">
            <span>100%</span>
            <span>50%</span>
            <span>0%</span>
          </div>
          <dl className={styles.rates}>
            {rates.map((metric) => {
              const valueLabel = metric.value === null ? "미측정" : isaacPercent(metric.value);
              const height = `${(metric.value ?? 0) * 100}%`;
              return (
                <div className={styles.rate} key={metric.key}>
                  <dt>{metric.label}</dt>
                  <dd className={styles.ratePlot}>
                    <Tooltip
                      content={
                        metric.term === "task_success"
                          ? ISAAC_TASK_SUCCESS_NOTE
                          : ISAAC_SUCCESS_NOTE
                      }
                      className={styles.plotTooltip}
                    >
                      <button
                        type="button"
                        className={styles.plotTrigger}
                        aria-label={`${metric.label} 그래프: ${valueLabel}`}
                      >
                        {metric.value !== null ? (
                          <span
                            className={styles.conditionFill}
                            data-emphasized={metric.term === "task_success"}
                            aria-hidden="true"
                            style={{ height }}
                          />
                        ) : null}
                        <strong className={styles.rateValue} style={{ bottom: height }}>
                          {valueLabel}
                        </strong>
                      </button>
                    </Tooltip>
                  </dd>
                  <dd className={styles.count}>
                    <ConditionCount metric={metric} termsState={termsState} />
                  </dd>
                  <dd className={styles.rateCriteria}>
                    <span className={styles.criteriaTitle}>성공 기준</span>
                    {metric.term === "task_success" ? (
                      <p className={styles.note}>Object와 Robot 조건을 모두 충족해야 합니다.</p>
                    ) : criteriaByTerm[metric.term].length > 0 ? (
                      <EvalSuccessCriteriaChips criteria={criteriaByTerm[metric.term]} />
                    ) : (
                      <span className={styles.note}>—</span>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        </figure>
      )}
      {metricsState.status !== "success" || criteria.length === 0 ? (
        <EvalSuccessCriteria
          execution={execution}
          metadataState={metadataState}
          onRetry={onRetry}
        />
      ) : additionalCriteria.length > 0 ? (
        <div className={styles.criteria}>
          <h4 className={styles.subtitle}>기타 성공 기준</h4>
          <EvalSuccessCriteriaChips criteria={additionalCriteria} />
        </div>
      ) : null}
      <IsaacStageList stages={stages} />
      {stages.length > 0 ? <p className={styles.note}>{ISAAC_STAGE_NOTE}</p> : null}
      <div className={styles.time}>
        성공 평균 시간 <strong>{successfulTimeLabel(termsState)}</strong>
      </div>
      <p className={styles.note}>{ISAAC_TIME_NOTE}</p>
    </EvalRunDetailSection>
  );
}

function ConditionCount({
  metric,
  termsState,
}: {
  metric: ReturnType<typeof isaacSuccessRates>[number];
  termsState: LoadState<TrainingEvalSuccessTermsView>;
}) {
  if (metric.value === null) return null;
  if (metric.counts !== null)
    return `충족 ${metric.counts.satisfied} / 측정 ${metric.counts.measured}개`;
  if (metric.countsMismatch) return "집계와 Episode 기록이 달라 측정 건수를 표시할 수 없습니다.";
  if (termsState.status === "error") return "측정 건수 조회 실패";
  if (termsState.status === "success") return "측정 건수 미제공";
  return "측정 건수 불러오는 중…";
}
